package link.erpango.quip.db

import app.cash.sqldelight.ExecutableQuery
import app.cash.sqldelight.Query
import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import kotlin.Any
import kotlin.Double
import kotlin.Long
import kotlin.String

public class VehicleDocumentQueries(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver) {
  public fun <T : Any> selectAll(mapper: (
    id: Long,
    server_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ) -> T): Query<T> = Query(1_217_190_131, arrayOf("VehicleDocument"), driver, "VehicleDocument.sq", "selectAll", """
  |SELECT VehicleDocument.id, VehicleDocument.server_id, VehicleDocument.vehicle_vin, VehicleDocument.name, VehicleDocument.file_path, VehicleDocument.file_type, VehicleDocument.created_at, VehicleDocument.size_mb, VehicleDocument.updated_at
  |FROM VehicleDocument
  """.trimMargin()) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1),
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getDouble(7)!!,
      cursor.getString(8)
    )
  }

  public fun selectAll(): Query<VehicleDocument> = selectAll(::VehicleDocument)

  public fun <T : Any> selectByVin(vehicle_vin: String, mapper: (
    id: Long,
    server_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ) -> T): Query<T> = SelectByVinQuery(vehicle_vin) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1),
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getDouble(7)!!,
      cursor.getString(8)
    )
  }

  public fun selectByVin(vehicle_vin: String): Query<VehicleDocument> = selectByVin(vehicle_vin, ::VehicleDocument)

  public fun <T : Any> selectByServerId(server_id: Long?, mapper: (
    id: Long,
    server_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ) -> T): Query<T> = SelectByServerIdQuery(server_id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1),
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getDouble(7)!!,
      cursor.getString(8)
    )
  }

  public fun selectByServerId(server_id: Long?): Query<VehicleDocument> = selectByServerId(server_id, ::VehicleDocument)

  public fun <T : Any> selectById(id: Long, mapper: (
    id: Long,
    server_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ) -> T): Query<T> = SelectByIdQuery(id) { cursor ->
    mapper(
      cursor.getLong(0)!!,
      cursor.getLong(1),
      cursor.getString(2)!!,
      cursor.getString(3)!!,
      cursor.getString(4)!!,
      cursor.getString(5)!!,
      cursor.getString(6)!!,
      cursor.getDouble(7)!!,
      cursor.getString(8)
    )
  }

  public fun selectById(id: Long): Query<VehicleDocument> = selectById(id, ::VehicleDocument)

  public fun last_insert_rowid(): ExecutableQuery<Long> = Query(236_231_270, driver, "VehicleDocument.sq", "last_insert_rowid", "SELECT last_insert_rowid()") { cursor ->
    cursor.getLong(0)!!
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun insertDocument(
    id: Long?,
    server_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ): Long {
    val result = driver.execute(1_209_841_094, """
        |INSERT OR REPLACE INTO VehicleDocument(id, server_id, vehicle_vin, name, file_path, file_type, created_at, size_mb, updated_at)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 9) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
          bindLong(parameterIndex++, server_id)
          bindString(parameterIndex++, vehicle_vin)
          bindString(parameterIndex++, name)
          bindString(parameterIndex++, file_path)
          bindString(parameterIndex++, file_type)
          bindString(parameterIndex++, created_at)
          bindDouble(parameterIndex++, size_mb)
          bindString(parameterIndex++, updated_at)
        }.await()
    notifyQueries(1_209_841_094) { emit ->
      emit("VehicleDocument")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun insert(
    server_id: Long?,
    vehicle_vin: String,
    name: String,
    file_path: String,
    file_type: String,
    created_at: String,
    size_mb: Double,
    updated_at: String?,
  ): Long {
    val result = driver.execute(1_915_239_307, """
        |INSERT INTO VehicleDocument(server_id, vehicle_vin, name, file_path, file_type, created_at, size_mb, updated_at)
        |VALUES (?, ?, ?, ?, ?, ?, ?, ?)
        """.trimMargin(), 8) {
          var parameterIndex = 0
          bindLong(parameterIndex++, server_id)
          bindString(parameterIndex++, vehicle_vin)
          bindString(parameterIndex++, name)
          bindString(parameterIndex++, file_path)
          bindString(parameterIndex++, file_type)
          bindString(parameterIndex++, created_at)
          bindDouble(parameterIndex++, size_mb)
          bindString(parameterIndex++, updated_at)
        }.await()
    notifyQueries(1_915_239_307) { emit ->
      emit("VehicleDocument")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteById(id: Long): Long {
    val result = driver.execute(-1_796_192_273, """
        |DELETE FROM VehicleDocument
        |WHERE id = ?
        """.trimMargin(), 1) {
          var parameterIndex = 0
          bindLong(parameterIndex++, id)
        }.await()
    notifyQueries(-1_796_192_273) { emit ->
      emit("VehicleDocument")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteAllByVin(vehicle_vin: String): Long {
    val result = driver.execute(-1_256_367_936, """
        |DELETE FROM VehicleDocument
        |WHERE vehicle_vin = ?
        """.trimMargin(), 1) {
          var parameterIndex = 0
          bindString(parameterIndex++, vehicle_vin)
        }.await()
    notifyQueries(-1_256_367_936) { emit ->
      emit("VehicleDocument")
    }
    return result
  }

  /**
   * @return The number of rows updated.
   */
  public suspend fun deleteAll(): Long {
    val result = driver.execute(-1_720_511_004, """DELETE FROM VehicleDocument""", 0).await()
    notifyQueries(-1_720_511_004) { emit ->
      emit("VehicleDocument")
    }
    return result
  }

  private inner class SelectByVinQuery<out T : Any>(
    public val vehicle_vin: String,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("VehicleDocument", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("VehicleDocument", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(1_489_904_406, """
    |SELECT VehicleDocument.id, VehicleDocument.server_id, VehicleDocument.vehicle_vin, VehicleDocument.name, VehicleDocument.file_path, VehicleDocument.file_type, VehicleDocument.created_at, VehicleDocument.size_mb, VehicleDocument.updated_at
    |FROM VehicleDocument
    |WHERE vehicle_vin = ?
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindString(parameterIndex++, vehicle_vin)
    }

    override fun toString(): String = "VehicleDocument.sq:selectByVin"
  }

  private inner class SelectByServerIdQuery<out T : Any>(
    public val server_id: Long?,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("VehicleDocument", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("VehicleDocument", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(null, """
    |SELECT VehicleDocument.id, VehicleDocument.server_id, VehicleDocument.vehicle_vin, VehicleDocument.name, VehicleDocument.file_path, VehicleDocument.file_type, VehicleDocument.created_at, VehicleDocument.size_mb, VehicleDocument.updated_at
    |FROM VehicleDocument
    |WHERE server_id ${ if (server_id == null) "IS" else "=" } ?
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, server_id)
    }

    override fun toString(): String = "VehicleDocument.sq:selectByServerId"
  }

  private inner class SelectByIdQuery<out T : Any>(
    public val id: Long,
    mapper: (SqlCursor) -> T,
  ) : Query<T>(mapper) {
    override fun addListener(listener: Query.Listener) {
      driver.addListener("VehicleDocument", listener = listener)
    }

    override fun removeListener(listener: Query.Listener) {
      driver.removeListener("VehicleDocument", listener = listener)
    }

    override fun <R> execute(mapper: (SqlCursor) -> QueryResult<R>): QueryResult<R> = driver.executeQuery(-921_770_304, """
    |SELECT VehicleDocument.id, VehicleDocument.server_id, VehicleDocument.vehicle_vin, VehicleDocument.name, VehicleDocument.file_path, VehicleDocument.file_type, VehicleDocument.created_at, VehicleDocument.size_mb, VehicleDocument.updated_at
    |FROM VehicleDocument
    |WHERE id = ?
    """.trimMargin(), mapper, 1) {
      var parameterIndex = 0
      bindLong(parameterIndex++, id)
    }

    override fun toString(): String = "VehicleDocument.sq:selectById"
  }
}
